/**
 * The discovery registry - every clickable thing in Megha's universe that
 * can be "found." Each entry has an id (matches the discoveryId on the
 * star module), a label (shown in the checklist when found), and a
 * category (used to group items in the panel).
 *
 * Hidden items show as "???" until discovered, then reveal their real
 * label. Other items show their label by name in the checklist along
 * with a "+ N more to find" hint for undiscovered items.
 */

export const DISCOVERIES = {
  people: {
    label: 'People',
    items: [
      { id: 'family-father',  label: 'Ishwarappa Mattikalli - your father' },
      { id: 'family-mother',  label: 'Sheela Haveri - your mother' },
      { id: 'family-brother', label: 'Ganesh Mattikalli - your brother' },
    ],
  },

  places: {
    label: 'Places',
    items: [
      { id: 'birth-star',       label: 'Your birth star' },
      { id: 'planet-kerala',    label: 'Kerala - first home' },
      { id: 'planet-bangalore', label: 'Bangalore - second home' },
      { id: 'planet-seattle',   label: 'Seattle - third home' },
    ],
  },

  hobbies: {
    label: 'Hobbies & loves',
    items: [
      { id: 'sirius',        label: 'Sirius - Jimmy' },
      { id: 'pulsar',        label: 'The Pulsar - dance' },
      { id: 'europa',        label: 'Europa - swimming' },
      { id: 'snowboarding',  label: 'Snowboarding' },
      { id: 'wanderer',      label: 'The Wanderer - wanderlust' },
      { id: 'radio-stars',   label: 'Radio Stars - BTS' },
    ],
  },

  hidden: {
    label: 'Hidden moments',
    items: [
      { id: 'first-conversation', label: 'Jab We Met' },
      { id: 'd-day',              label: 'D-Day' },
      { id: '40-eridani',         label: '40 Eridani' },
      { id: 'tau-ceti',           label: 'Tau Ceti' },
      { id: 'hail-mary',          label: 'The Hail Mary' },
      { id: 'astrophage',         label: 'Astrophage' },
      { id: 'xenonite',           label: 'Xenonite' },
      { id: 'fistbump',           label: 'Fistbump' },
    ],
  },
};

/** Poetic hint clues for each discoverable item. */
export const HINTS = {
  'family-father':     'A steady golden light in the upper sky. He does not flicker.',
  'family-mother':     'Two stars, very close, pulsing in opposite rhythms.',
  'family-brother':    'A smaller star near the other two. Same corner of the sky.',
  'birth-star':        'A pale yellow star. The light from it is exactly 28 years old.',
  'planet-kerala':     'A green dot. The first home.',
  'planet-bangalore':  'An amber dot. The second home.',
  'planet-seattle':    'A blue dot. The third home.',
  'sirius':            'The brightest star in the sky. You cannot miss it.',
  'pulsar':            'A blue-white star that pulses fast. It has rhythm.',
  'europa':            'An icy blue point near a bright warm star.',
  'snowboarding':      'A cold blue star in the upper north.',
  'wanderer':          'A warm amber star, drifting alone.',
  'radio-stars':       'A cluster of purple-pink stars. There are seven.',
  'first-conversation': 'Two dim golden points near Bangalore, very close together.',
  'd-day':             'A single dim golden point, alone in the south-southwest.',
  '40-eridani':        'A real star. Rocky would know where to look.',
  'tau-ceti':          'A real star. The destination.',
  'hail-mary':         'A tiny dim point, almost directly overhead, far from everything.',
  'astrophage':        'A faint glow near Jupiter that should not be there.',
  'xenonite':          'Something small and careful, hiding in the west.',
  'fistbump':          'Two points in the west, side by side. They never touch.',
};

/** Total number of items across all categories. */
export function totalDiscoverable() {
  return Object.values(DISCOVERIES).reduce((s, cat) => s + cat.items.length, 0);
}

/** Find the entry for a given discoveryId, or null. */
export function findDiscovery(id) {
  for (const [categoryKey, category] of Object.entries(DISCOVERIES)) {
    const item = category.items.find((i) => i.id === id);
    if (item) return { ...item, category: categoryKey, categoryLabel: category.label };
  }
  return null;
}
